import { useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getAvailableUserPermissions } from 'lib/users';
import {
  Badge,
  DisplayFormattedDate,
  EmptyIndicator,
  MoreOptions,
  Pagination,
  User
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  AdminEdit,
  AdminsHeader,
  AdminsRemove,
  AdminsSubHeader,
  AdminsTransferRights
} from '.';

import type { AdminUser, User as UserType } from 'shared-types';

export type AdminsFilters = {
  page: number;
  permission: 'all' | UserType['permissions'][number];
  role: 'all' | 'admin' | 'superadmin';
  search: string;
};

const Admins = () => {
  const { messages, serverConfig } = useReduxState();

  const [filters, setFilters] = useState<AdminsFilters>({
    page: 1,
    permission: 'all',
    role: 'all',
    search: ''
  });

  const { data, loading, updateData } = useApiData({
    url:
      `/admin/admins?page=${filters.page}` +
      (filters.permission !== 'all'
        ? `&permission=${filters.permission}`
        : '') +
      (filters.role !== 'all' ? `&role=${filters.role}` : '') +
      (filters.search ? `&search=${filters.search}` : '')
  });

  const handleFilters = (params: Partial<AdminsFilters>) => {
    setFilters({ ...filters, ...params, page: 1 });
  };

  const permissions = getAvailableUserPermissions({ messages, serverConfig });

  let { users: admins } = data || {};
  admins = admins?.filter((admin: AdminUser) => admin.id !== 1);

  return (
    <div className="admins">
      <Box
        headerContent={
          <AdminsHeader
            loading={loading}
            existingAdminIds={data?.existingAdminIds || []}
            numberOfAdmins={data?.numberOfAdmins || 0}
            numberOfSuperadmins={data?.numberOfSuperadmins || 0}
            updateAdmins={updateData}
          />
        }
        subHeaderContent={<AdminsSubHeader updateFilters={handleFilters} />}
        loading={loading}
        padding={0}
      >
        {admins?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell>{messages.commonUser}</BoxTableCell>
                <BoxTableCell>{messages.usersRole}</BoxTableCell>
                <BoxTableCell>{messages.adminSupervisor}</BoxTableCell>
                <BoxTableCell>{messages.usersPermissions}</BoxTableCell>
                <BoxTableCell>{messages.usersLastLogin}</BoxTableCell>
                <BoxTableCell>{'2FA'}</BoxTableCell>
                <BoxTableCell></BoxTableCell>
              </BoxTableHeader>

              {admins.map((user) => (
                <BoxTableRow key={user.id}>
                  <BoxTableCell>
                    <User
                      enableProfileLink={true}
                      style={{ minWidth: 200 }}
                      user={user}
                    />
                  </BoxTableCell>

                  <BoxTableCell label={messages.usersRole}>
                    {user.roles.includes('vuoadmin') ? (
                      <Badge size="small" text={'Vuoadmin'} />
                    ) : user.roles.includes('superadmin') ? (
                      <Badge size="small" text={messages.usersSuperadmin} />
                    ) : user.roles.includes('admin') ? (
                      <Badge size="small" text={messages.usersAdmin} />
                    ) : null}
                  </BoxTableCell>

                  <BoxTableCell label={messages.adminSupervisor}>
                    {user.roles.includes('supervisor') ? (
                      <Badge size="small" text={messages.commonYes} />
                    ) : null}
                  </BoxTableCell>

                  <BoxTableCell label={messages.usersPermissions}>
                    {user.permissions
                      ?.filter((permission) => permissions[permission])
                      .map((permission) => (
                        <div
                          key={permission}
                          style={{
                            display: 'inline-block',
                            margin: '3px 8px 3px 0'
                          }}
                        >
                          <Badge
                            size="small"
                            text={permissions[permission]?.label}
                          />
                        </div>
                      ))}
                  </BoxTableCell>

                  <BoxTableCell label={messages.usersLastLogin}>
                    <DisplayFormattedDate date={user.lastLogin} />
                  </BoxTableCell>

                  <BoxTableCell label={'2FA'}>
                    {user.twoFactorAuthIsEnabled ? (
                      <Badge
                        category="success-light"
                        text={messages.commonEnabled}
                      />
                    ) : null}
                  </BoxTableCell>

                  <BoxTableCell shrink={true}>
                    {user.id !== 1 ? (
                      <MoreOptions
                        renderAsFixed={true}
                        styleContentButtons={false}
                      >
                        <AdminEdit
                          numberOfAdmins={data?.numberOfAdmins || 0}
                          numberOfSuperadmins={data?.numberOfSuperadmins || 0}
                          targetUser={user}
                          updateAdmins={updateData}
                        />
                        <AdminsTransferRights
                          targetUser={user}
                          updateAdmins={updateData}
                        />
                        <AdminsRemove
                          targetUser={user}
                          updateAdmins={updateData}
                        />
                      </MoreOptions>
                    ) : null}
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator
            icon="user-shield"
            title={messages.usersNoAdminsFound}
          />
        )}
      </Box>

      <Pagination
        currentPage={filters.page}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => setFilters({ ...filters, page })}
      />
    </div>
  );
};

export default Admins;
